import { useCallback, useRef } from 'react';
import _ from 'lodash';

import { DisplayControlType } from 'utils/constants';

export const useDebouncedStartTyping = (fn, formId, fieldId, isNewRecord, recordId) => {
  const callback = useCallback(() => {
    let variables = { fieldId };
    if (isNewRecord) {
      variables = { ...variables, templateId: formId, recordId };
    } else {
      variables.assessmentId = formId;
    }
    fn({ variables }).catch(resErr => {
      console.error('Start typing error :(', resErr);
    });
  }, [formId, fieldId, fn, isNewRecord, recordId]);
  return useRef(_.debounce(callback, 1000, { leading: true, trailing: false })).current;
};

export const useDebouncedStopTyping = (fn, formId, fieldId, isNewRecord, recordId) => {
  const callback = useCallback(() => {
    let variables = { fieldId };
    if (isNewRecord) {
      variables = { ...variables, templateId: formId, recordId };
    } else {
      variables.assessmentId = formId;
    }
    fn({ variables }).catch(resErr => {
      console.error('Stop typing error :(', resErr);
    });
  }, [formId, fieldId, fn, isNewRecord, recordId]);
  return useRef(_.debounce(callback, 2500, { leading: false, trailing: true })).current;
};

export const FieldTypes = {
  TEXT_FIELD: 'TEXT',
  NUMERIC_FIELD: 'NUMERIC',
  VALUES_LIST_FIELD: 'VALUES_LIST',
  DATE_FIELD: 'DATE',
  ATTACHMENT_FIELD: 'ATTACHMENT'
};

export function generateUpdateVariables(assessmentId, fieldId, value, otherText) {
  return {
    variables: {
      assessmentId,
      fieldId,
      value,
      otherText
    }
  };
}

export function generateTemplateUpdateVariables(templateId, recordId, fieldId, value, otherText) {
  return {
    variables: {
      templateId,
      recordId,
      fieldId,
      value,
      otherText
    }
  };
}

export const getQuestionValue = question => {
  switch (question.fieldType) {
    case FieldTypes.ATTACHMENT_FIELD:
      return question.attachmentValue;
    case FieldTypes.TEXT_FIELD:
      return question.textValue;
    case FieldTypes.NUMERIC_FIELD:
      return question.numericValue;
    case FieldTypes.VALUES_LIST_FIELD: {
      return question.listValue;
    }
    case FieldTypes.DATE_FIELD: {
      return question.dateValue;
    }
    default:
      return null;
  }
};

export const getQuestionOtherText = question => {
  return question.fieldType === FieldTypes.VALUES_LIST_FIELD ? question.otherText : null;
};

export const stringToHTML = str => {
  const dom = document.createElement('div');
  dom.innerHTML = str;
  return dom;
};

export const isSameNode = (oldValue, newValue) => {
  const oldNode = stringToHTML(oldValue);
  const newNode = stringToHTML(newValue);
  return oldNode.isEqualNode(newNode);
};

export const isSameValue = (question, value, otherText = null) => {
  const oldValue = getQuestionValue(question);
  const oldOtherText = getQuestionOtherText(question);

  let sameValue = false;
  const sameOtherText = oldOtherText === otherText;

  if (Array.isArray(oldValue)) {
    sameValue = _.xor(oldValue, value).length === 0;
  } else if (typeof oldValue === 'number') {
    sameValue = _.toString(oldValue) === _.toString(value);
  } else {
    sameValue = question.displayControlType === DisplayControlType.TEXTAREA ? isSameNode(oldValue, value) : oldValue === value;
  }

  return sameValue && sameOtherText;
};

export default {
  useDebouncedStartTyping,
  useDebouncedStopTyping,
  generateUpdateVariables,
  generateTemplateUpdateVariables,
  getQuestionValue,
  isSameValue,
  stringToHTML
};
